import { Json, type JsonObject } from "../core/json.ts";
import type { Option } from "../core/option.ts";
import type { SheetsNetwork } from "../wire/rows-network.ts";

/**
 * The one update-patch vocabulary every update verb of the SDK speaks
 * (`row.update` / `RowPatch`, `events.update` / `EventPatch`,
 * `tasks.update` / `TasksClient.Patch`, `sheets.update` /
 * `SheetsClient.Patch`): a field the patch names is SET to its value, or —
 * where the server can go without it — CLEARED with `Patch.clear`; a field
 * the patch omits is left as it is. `null` and `undefined` are not patch
 * values.
 *
 * ```ts
 * await row.update({ label: "Weight", icon: Patch.clear }, Actor.EXTERNAL);
 * ```
 */
export namespace Patch {
  /** The value a clearable field takes to clear it (`{ icon: Patch.clear }`). */
  export const clear: unique symbol = Symbol("Patch.clear");
  /** The type of `Patch.clear`. */
  export type Clear = typeof clear;
  /** What a field that can be cleared takes: the value, or `Patch.clear`. */
  export type Clearable<T> = T | Clear;

  /** A clearable field's wire value: `null` for `Patch.clear`. */
  export function wire<T>(value: Clearable<T>): Option<T> {
    return value === clear ? null : value;
  }

  /** The patch value of a wire field: `null` is `Patch.clear`. */
  export function fromWire<T>(value: Option<T>): Clearable<T> {
    return value ?? clear;
  }
}

/**
 * A change to a row's own fields, as the row update verbs take it
 * (`row.update(patch, actor)`, `row.updateDurable(patch, actor)`): each
 * field the patch names is SET — or, where the row can go without one,
 * CLEARED (`Patch.clear`); a field it does not name is left as it is.
 *
 * It lists exactly the fields a held row paints before the server answers
 * and takes back on a refusal:
 *
 * - `label`, `icon`, `formula` — a value sets it; `Patch.clear` removes it.
 * - `heightPx` — a value sets it (clamped to the row type's bounds);
 *   `Patch.clear` goes back to the type's default height.
 * - `timeScale` — set-only: a row always has a time scale, so there is no
 *   clear (the wire's `timeScale: null` is refused by `fromWire`).
 * - `attributes` — the WHOLE bag, replaced (the server stores the bag it is
 *   sent); `{}` empties it. To change some keys and keep the rest, use
 *   `row.mergeAttributes(patch, actor)`, which composes the bag.
 *
 * The lookup key is not a patch field: `row.setLookupKey(key, actor)` also
 * re-registers the row under the new key.
 *
 * ```ts
 * await row.update({ label: "Weight", icon: Patch.clear }, Actor.EXTERNAL);
 * ```
 */
export type RowPatch = {
  readonly label?: Patch.Clearable<string>;
  readonly icon?: Patch.Clearable<string>;
  readonly heightPx?: Patch.Clearable<number>;
  readonly formula?: Patch.Clearable<string>;
  readonly timeScale?: SheetsNetwork.TimeScaleCustom;
  readonly attributes?: JsonObject;
};

/** Values that go with `RowPatch`: `encode`, `fromWire`. */
export namespace RowPatch {
  /**
   * The row PATCH body (`PATCH /api/rows/{rowKey}`) of `patch` — the one
   * encoder: a set field carries its value, a cleared one `null`, a field
   * the patch does not name no key. A fresh object on every call.
   */
  export function encode(patch: RowPatch): SheetsNetwork.PatchRow_PendingValidation {
    const body: SheetsNetwork.PatchRow_PendingValidation = {};
    if (patch.label !== undefined) body.label = Patch.wire(patch.label);
    if (patch.icon !== undefined) body.icon = Patch.wire(patch.icon);
    if (patch.heightPx !== undefined) body.heightPx = Patch.wire(patch.heightPx);
    if (patch.formula !== undefined) body.formula = Patch.wire(patch.formula);
    if (patch.timeScale !== undefined) body.timeScale = patch.timeScale;
    if (patch.attributes !== undefined) body.attributes = patch.attributes;
    return body;
  }

  /**
   * The patch a wire body (`SheetsNetwork.PatchRow_PendingValidation`, as a
   * command line or a tool argument carries it) describes: a value sets,
   * `null` clears, an absent key leaves the field. Throws `TypeError` for a
   * body the update verbs cannot send: a `lookupKey` (set it with
   * `row.setLookupKey`), a `timeScale: null` (set-only), or `attributes`
   * that are not a JSON object.
   */
  export function fromWire(body: SheetsNetwork.PatchRow_PendingValidation): RowPatch {
    if (named(body, "lookupKey")) {
      throw new TypeError("RowPatch.fromWire: lookupKey is not a patch field (row.setLookupKey(key, actor))");
    }
    if (named(body, "timeScale") && body.timeScale == null) {
      throw new TypeError("RowPatch.fromWire: timeScale cannot be cleared (set it, or leave it out)");
    }
    let attributes: Option<JsonObject> = null;
    if (named(body, "attributes")) {
      const bag = body.attributes ?? null;
      attributes = bag == null ? {} : Json.object(bag);
      if (attributes == null) throw new TypeError("RowPatch.fromWire: attributes must be a JSON object");
    }
    return {
      ...(named(body, "label") ? { label: Patch.fromWire(body.label ?? null) } : {}),
      ...(named(body, "icon") ? { icon: Patch.fromWire(body.icon ?? null) } : {}),
      ...(named(body, "heightPx") ? { heightPx: Patch.fromWire(body.heightPx ?? null) } : {}),
      ...(named(body, "formula") ? { formula: Patch.fromWire(body.formula ?? null) } : {}),
      ...(body.timeScale != null ? { timeScale: body.timeScale } : {}),
      ...(attributes != null ? { attributes } : {}),
    };
  }
}

/** Whether `body` names `key` (present, `null` included). */
function named(body: SheetsNetwork.PatchRow_PendingValidation, key: keyof SheetsNetwork.PatchRow_PendingValidation): boolean {
  return Object.prototype.hasOwnProperty.call(body, key) && body[key] !== undefined;
}
